import React from 'react';
import PropTypes from 'prop-types';
import { SecondaryActionSelect, SecondaryActionButton, Stack } from '@facet-wealth/design';
import HorizontalStack from 'Components/ComponentLibrary/Atoms/Layout/HorizontalStack';
import StackItem from 'Components/ComponentLibrary/Atoms/Layout/StackItem';
import VerticalStack from 'Components/ComponentLibrary/Atoms/Layout/VerticalStack';
import Text from 'Components/ComponentLibrary/Atoms/Text';
import Transition from 'Components/ComponentLibrary/Atoms/Transition';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box/Box';
import InflationToggle from 'Views/Page/Content/Templates/WhatIfTemplate/Components/InflationToggle/InflationToggle';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import UpdatedScenarioChart from 'Components/ComponentLibrary/Organisms/UpdatedScenarioChart';
import moment from 'moment';
import useMobileLayout from 'Hooks/useMobileLayout';
import { TIMEFRAME_QP } from 'Components/ComponentLibrary/Organisms/ScenarioChart/ScenarioChart';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import useAccordion from './useAccordion';

const FEATURE_DESCRIPTION = 'facet plan scenario chart';

const ScenarioChartContent = ({
  broadcastTimeframe,
  chartDataChartId,
  chartDataResource,
  chartDataTimeframe,
  hasNoTitle,
  height,
  isCollapsible,
  isInflationAdjustable,
  isMultiChart,
  query,
  setChartDataChartId,
  setChartDataTimeframe,
  setChartDataUsePresentData,
  usePresentData,
  isChartRefreshing,
}) => {
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const { collapse, expand, isCollapsed } = useAccordion();
  const isMobile = useMobileLayout();

  const timeframeOptions = React.useMemo(() => {
    return chartDataResource
      .meta()
      .query(query)
      .field(TIMEFRAME_QP)
      .options()
      .list()
      .map(option => ({
        value: option.attr('id'),
        label: option.attr('prompt'),
      }));
  }, [chartDataResource]);

  const chartOptions = React.useMemo(() => {
    if (!isMultiChart) {
      return [];
    }

    return chartDataResource
      .meta()
      .query(query)
      .field('chartId')
      .options()
      .list()
      .map(option => ({
        value: option.attr('id'),
        label: option.attr('prompt'),
      }));
  }, [chartDataResource]);

  const handleChartVisibilityToggle = () => {
    if (isCollapsed) {
      expand();
    } else {
      collapse();
    }
  };

  const maybeHandleSelect = React.useMemo(() => {
    const isTouchDevice = 'ontouchstart' in window;

    if (isTouchDevice) {
      return undefined;
    }

    const onChange = broadcastTimeframe ?? setChartDataTimeframe;

    const handler = (args) => {
      const value = moment.utc(args[0]?.x())?.year();
      const option = timeframeOptions.find(timeframeOption => {
        return String(value).startsWith(timeframeOption.value);
      });

      if (option) {
        onChange(option.value);
      }
    };

    return handler;
  }, [broadcastTimeframe, setChartDataTimeframe, timeframeOptions]);

  return (
    <VerticalStack>
      <StackItem useZeroFontSize={false}>
        <Box
          paddingY={1}
          display={'flex'}
          flexDirection={'column'}
        >
          {hasNoTitle ? null : (
            <Text
              align="left"
              color="black"
              text={chartDataResource.attr('title')}
              weight="medium"
              variant={isMobile ? 'h4' : 'h2'}
            />
          )}
          <Box fontSize={'1rem'} marginTop={1}>
            <Stack justifyContent={'space-between'} alignItems={'center'}>
              <HorizontalStack alignment="centered" spacing={1} wrap>
                {isInflationAdjustable && (
                <StackItem hidden={isCollapsed}>
                  <InflationToggle
                    onChange={(value) => {
                      setChartDataUsePresentData(value);
                      const action = value ? 'show present values' : 'show future values';
                      logStandardizedEvent(categories.whatIf, eventTypes.toggleSwitch, `${FEATURE_DESCRIPTION} ${action}`);
                    }}
                    usePresentData={usePresentData}
                  />
                </StackItem>
                )}
                {isMultiChart && chartOptions.length > 1 && (
                <StackItem hidden={isCollapsed} useZeroFontSize={false}>
                  <SecondaryActionSelect
                    onChange={(value) => {
                      setChartDataChartId(value);
                    }}
                    options={chartOptions}
                    label={'Chart'}
                    value={chartDataChartId}
                  />
                </StackItem>
                )}
                <StackItem hidden={isCollapsed} useZeroFontSize={false}>
                  <SecondaryActionSelect
                    onChange={(value) => {
                      setChartDataTimeframe(value);
                    }}
                    options={timeframeOptions}
                    label={'Time Frame'}
                    value={chartDataTimeframe}
                  />
                </StackItem>
                {isChartRefreshing ? (
                  <StackItem hidden={isCollapsed}>
                    <LoadingIndicator size={20} spacing={false} />
                  </StackItem>
                ) : null}
              </HorizontalStack>
              {isCollapsible && (
              <StackItem>
                <SecondaryActionButton
                  icon={isCollapsed ? 'keyboardArrowDown' : 'keyboardArrowUp'}
                  label={isCollapsed ? 'Show Chart' : 'Close Chart'}
                  onClick={handleChartVisibilityToggle}
                  size={'sm'}
                />
              </StackItem>
              )}
            </Stack>
          </Box>
        </Box>
      </StackItem>
      <StackItem>
        <Transition in={!isCollapsed} variant="collapse">
          <Box
            height={height}
            width="100%"
            display="flex"
            flexDirection="column"
          >
            <UpdatedScenarioChart
              chartDataResource={chartDataResource}
              onClick={maybeHandleSelect}
              showLegend
            />
          </Box>
        </Transition>
      </StackItem>
    </VerticalStack>
  );
};

ScenarioChartContent.defaultProps = {
  broadcastTimeframe: undefined,
  isInflationAdjustable: false,
  setChartDataTimeframe: undefined,
  chartDataChartId: null,
};

ScenarioChartContent.propTypes = {
  broadcastTimeframe: PropTypes.func,
  chartDataChartId: PropTypes.string,
  chartDataResource: PropTypes.object.isRequired,
  chartDataTimeframe: PropTypes.string.isRequired,
  hasNoTitle: PropTypes.bool.isRequired,
  height: PropTypes.number.isRequired,
  isCollapsible: PropTypes.bool.isRequired,
  isInflationAdjustable: PropTypes.bool,
  isMultiChart: PropTypes.bool.isRequired,
  query: PropTypes.string.isRequired,
  setChartDataChartId: PropTypes.func.isRequired,
  setChartDataTimeframe: PropTypes.func,
  setChartDataUsePresentData: PropTypes.func.isRequired,
  usePresentData: PropTypes.bool.isRequired,
  isChartRefreshing: PropTypes.bool.isRequired,
};

export default ScenarioChartContent;
